import { useState, useEffect } from "react";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

import MDBox from "../../../components/MDBox";
import MDModal from "../../../components/MDModal";
import MDTypography from "../../../components/MDTypography";
import MDButton from "../../../components/MDButton";

import moment from "moment";

import { putReserve, postReserve, getReserve, deleteReserve } from "../../../api/reserves";

import ReserveInfos from "./ReserveInfos";

export default function CreateEditReserves({
  edit = false,
  type = null,
  reserves,
  setReserves,
  device,
}) {
  const [open, setOpen] = useState(false);

  const [step, setStep] = useState(0);
  const steps = edit ? reserves.map((_, i) => `reserve ${i + 1}`) : ["reserve 1"];
  const isLastStep = step === steps.length - 1;
  const isFirstStep = step === 0;

  const [deviceId, setDeviceId] = useState("");
  const [reserveId, setReseveId] = useState("");
  const [reserveDescription, setReserveDescription] = useState("");
  const [reserveDate, setReserveDate] = useState(null);
  const [removalDate, setRemovalDate] = useState(null);

  useEffect(() => {
    if (edit && reserves && reserves[step] !== undefined && device) {
      setDeviceId(device.device_id);
      setReseveId(reserves[step].reserve_id);
      setReserveDescription(reserves[step].reserve_description);
      setReserveDate(reserves[step].reserve_date);
      setRemovalDate(reserves[step].removal_date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, reserves]);

  async function createReserveAPI() {
    const reserve = {
      device_id: device.device_id,
      reserve_description: reserveDescription,
      reserve_date: reserveDate ? moment(reserveDate).format("YYYY-MM-DD") : null,
      removal_date: removalDate ? moment(removalDate).format("YYYY-MM-DD") : null,
      reserve_type: type,
    };
    await postReserve(reserve);
  }

  async function deleteReserveAPI() {
    await deleteReserve(reserveId);
  }

  async function updateReserveAPI() {
    const reserve = {
      device_id: device.device_id,
      reserve_description: reserveDescription,
      reserve_date: reserveDate ? moment(reserveDate).format("YYYY-MM-DD") : null,
      removal_date: removalDate ? moment(removalDate).format("YYYY-MM-DD") : null,
    };
    await putReserve(reserve, reserveId);
  }

  async function fetchReserves() {
    const response = await getReserve(device.device_id, type);
    setReserves(response.data);
  }

  useEffect(() => {
    fetchReserves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetData() {
    setReserveDescription("");
    setReserveDate(null);
    setRemovalDate(null);
  }

  async function deleteBill() {
    await deleteReserveAPI();
    await fetchReserves();
    exit();
  }

  async function cancel() {
    if (edit) {
      await fetchReserves();
    } else {
      resetData();
    }
    exit();
  }

  async function submit() {
    if (edit) {
      await updateReserveAPI();
      await fetchReserves();
    } else {
      await createReserveAPI();
      await fetchReserves();
      resetData();
    }
    exit();
  }

  function exit() {
    setStep(0);
    setOpen(false);
  }

  const next = () => {
    setStep(step + 1);
  };

  const previous = () => setStep(step - 1);

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  return (
    <>
      {edit ? (
        <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
          <Icon style={{ color: darkMode ? "#344767" : "#FFF" }}>edit</Icon>
        </IconButton>
      ) : (
        <IconButton size="medium" disableRipple color="inherit" onClick={() => setOpen(true)}>
          <Icon style={{ color: darkMode ? "#344767" : "#FFF" }}>add</Icon>
        </IconButton>
      )}
      <MDModal open={open} onClose={() => setOpen(false)}>
        <MDBox mt={25} mb={9}>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={8}>
              <Card>
                <MDBox
                  mt={edit ? -12 : -5}
                  mb={2}
                  mx={2}
                  variant="gradient"
                  sx={{
                    background: darkMode ? "white" : "linear-gradient(195deg, #364c70, #273956)",
                    boxShadow: darkMode ? "0 1px 6px 1px white" : "0 1px 6px 1px #344767",
                  }}
                  borderRadius="lg"
                  textAlign="center"
                >
                  <MDBox mt={2} mb={edit ? 1 : 2}>
                    <MDBox>
                      {edit ? (
                        <MDTypography
                          variant="h3"
                          fontWeight="bold"
                          color={darkMode ? "inherit" : "white"}
                        >
                          {type === "Client"
                            ? "Modification des réserves client"
                            : "Modification des réserves organisme CE"}
                        </MDTypography>
                      ) : (
                        <MDTypography
                          variant="h3"
                          fontWeight="bold"
                          color={darkMode ? "inherit" : "white"}
                        >
                          {type === "Client"
                            ? "Ajouter d'une nouvelle réserve client"
                            : "Ajouter d'une nouvelle réserve organisme CE"}
                        </MDTypography>
                      )}
                    </MDBox>
                    <MDTypography
                      variant="h5"
                      fontWeight="regular"
                      color={darkMode ? "inherit" : "white"}
                    >
                      Veuillez rentrer les informations relatives à la réserve
                    </MDTypography>
                  </MDBox>
                  {edit && (
                    <Stepper
                      sx={{ boxShadow: "none", backgroundImage: "none" }}
                      activeStep={step}
                      alternativeLabel
                    >
                      {steps.map((label) => (
                        <Step
                          sx={{
                            "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-active":
                              {
                                color: darkMode && "#344767 !important",
                              },
                            "& .MuiStepConnector-root.MuiStepConnector-horizontal.MuiStepConnector-alternativeLabel.Mui-completed":
                              {
                                color: darkMode && "#344767 !important",
                              },
                          }}
                          key={label}
                        >
                          <StepLabel
                            sx={{
                              "& .MuiStepLabel-label.Mui-active": {
                                color: darkMode && "#344767 !important",
                              },
                              "& .MuiStepLabel-label.Mui-completed": {
                                color: darkMode && "#344767 !important",
                              },
                              "& .MuiStepIcon-root.Mui-active": {
                                background: darkMode && "#344767",
                                fill: darkMode && "#344767",
                                stroke: darkMode && "#344767",
                                borderColor: darkMode && "#344767",
                                boxShadow: darkMode && "0rem 0rem 0rem 0.125rem #344767",
                              },
                              "& .MuiStepIcon-root.Mui-completed": {
                                background: darkMode && "#344767",
                                fill: darkMode && "#344767",
                                stroke: darkMode && "#344767",
                                borderColor: darkMode && "#344767",
                                boxShadow: darkMode && "0rem 0rem 0rem 0.125rem #344767",
                              },
                            }}
                          >
                            {label}
                          </StepLabel>
                        </Step>
                      ))}
                    </Stepper>
                  )}
                </MDBox>
                <MDBox p={2}>
                  <MDBox>
                    <ReserveInfos
                      edit={edit}
                      deviceId={deviceId}
                      reserveDescription={reserveDescription}
                      setReserveDescription={setReserveDescription}
                      reserveDate={reserveDate}
                      setReserveDate={setReserveDate}
                      removalDate={removalDate}
                      setRemovalDate={setRemovalDate}
                    />
                    <MDBox mt={3} width="100%" display="flex" justifyContent="space-between">
                      <MDBox width="25%">
                        {isFirstStep ? (
                          <MDBox />
                        ) : (
                          <MDButton variant="gradient" color="warning" onClick={previous}>
                            Précédent
                          </MDButton>
                        )}
                      </MDBox>
                      <MDBox textAlign="right" width="75%">
                        <Grid container justifyContent="right" spacing={1.5}>
                          {isLastStep && steps.length > 0 ? (
                            <MDBox />
                          ) : (
                            <Grid item>
                              <MDButton variant="gradient" color="info" onClick={next}>
                                Suivant
                              </MDButton>
                            </Grid>
                          )}
                          <Grid item>
                            <MDButton variant="gradient" color="warning" onClick={cancel}>
                              Annuler
                            </MDButton>
                          </Grid>
                          <Grid item>
                            <MDButton variant="gradient" color="success" onClick={submit}>
                              Envoyer
                            </MDButton>
                          </Grid>
                          {edit && (
                            <Grid item>
                              <MDButton variant="gradient" color="error" onClick={deleteBill}>
                                Supprimer la situation
                              </MDButton>
                            </Grid>
                          )}
                        </Grid>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDModal>
    </>
  );
}
